import axios from "axios";
import { getDeviceId } from "@/lib/deviceId";

// ---- API base resolution ----------------------------------------------------
// Rule: same-origin `/api/v1` is preferred (it survives multi-hostname preview
// setups). If the current window origin does NOT route `/api/v1/*` to the
// backend (i.e. we're on a pure frontend preview URL), we fall back to
// REACT_APP_BACKEND_URL. No hostname is ever hard-coded here.
//
// v2 backend: the Spring Boot backend serves the versioned contract under
// /api/v1 (the Python FastAPI service used unversioned /api).
//
// We only decide once at module load — the base URL is stable per browser tab.
export const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function computeApiBase() {
  // SSR / non-browser safety fallback: use the configured absolute URL.
  if (typeof window === "undefined" || !window.location) {
    return `${CONFIGURED_BACKEND_URL}/api/v1`;
  }

  const currentOrigin = window.location.origin;

  // If the configured backend URL parses and shares the current origin, use
  // relative `/api/v1`. This is the intended prod shape (ingress routes /api/*
  // and /* to the same hostname).
  try {
    if (CONFIGURED_BACKEND_URL) {
      const backendOrigin = new URL(CONFIGURED_BACKEND_URL).origin;
      if (backendOrigin === currentOrigin) return "/api/v1";
      // Different origin: use the absolute configured URL (cross-origin, CORS
      // handles it — backend allow_origins="*").
      return `${CONFIGURED_BACKEND_URL}/api/v1`;
    }
  } catch (_e) {
    /* fall through */
  }

  // No configured URL — assume same-origin ingress.
  return "/api/v1";
}

export const API_BASE = computeApiBase();

// Debug snapshot for the Sync panel / support diagnostics.
export const RUNTIME_INFO = {
  window_origin: typeof window !== "undefined" ? window.location.origin : "",
  configured_backend_url: CONFIGURED_BACKEND_URL,
  effective_api_base: API_BASE,
  same_origin: API_BASE.startsWith("/"),
};

export const TOKEN_KEY = "tulsi_token";
export const USER_KEY = "tulsi_user";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}
export function setStoredUser(user) {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}
export function clearAuth() {
  setToken(null);
  setStoredUser(null);
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Phase 24A — send app build id on every request so backend can log it on
  // login and other write mutations. Frontend defaults to `dev-local` when
  // REACT_APP_BUILD_ID is not injected at build time.
  const buildId = process.env.REACT_APP_BUILD_ID || "dev-local";
  config.headers["X-App-Build"] = buildId;
  // Single-device binding: the server compares this at login.
  config.headers["X-Device-Fp"] = getDeviceId();
  return config;
});

// ---- error envelope normalization -------------------------------------------
// The v2 (Spring Boot) backend answers with a unified envelope:
//   {"error": {"code": "...", "message": "...", "details": <object|array>?}}
// The legacy FastAPI service used several `detail` shapes that ~20 call sites
// (and formatApiError) still consume. Translate once, centrally, so no caller
// needs to know which backend it is talking to:
//   details object  → detail = that object   (Python HTTPException detail obj)
//   details array   → detail = [{loc, msg}]  (Python 422 validation array)
//   no details      → detail = message       (Python string detail)
//   top-level {error: string, message} etc.  → left untouched (already legacy).
function normalizeErrorEnvelope(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return;
  if (data.detail !== undefined) return; // already a legacy-shaped payload
  const env = data.error;
  if (!env || typeof env !== "object" || Array.isArray(env)) return;
  if (typeof env.message !== "string" && env.details === undefined) return;
  const details = env.details;
  if (Array.isArray(details)) {
    // Validation details: [{field, message}] → FastAPI 422 [{loc, msg, type}].
    data.detail = details.map((d) =>
      d && typeof d === "object" && d.field !== undefined
        ? { loc: ["body", d.field], msg: d.message || "invalid", type: "value_error" }
        : d
    );
  } else if (details && typeof details === "object") {
    data.detail = details;
  } else if (typeof env.message === "string") {
    data.detail = env.message;
  }
}

// Central 401/428 handler. Callers can pass { skipAuthRedirect: true } to opt out.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    normalizeErrorEnvelope(error);
    const cfg = error.config || {};
    if (cfg.skipAuthRedirect) return Promise.reject(error);
    const status = error.response?.status;
    const path = window.location.pathname;
    if (status === 401) {
      clearAuth();
      if (path !== "/login") window.location.assign("/login");
    } else if (status === 428) {
      // A 428 here means "must_change_password was true when THIS request
      // was sent" — but this app has several background pollers sharing
      // this same axios instance (attendance poll, location heartbeat,
      // notifications, …), so a request fired just before the user
      // successfully changes their password can still be in flight and
      // land AFTER ChangePassword.jsx already flipped must_change_password
      // to false and navigated away. Hard-redirecting on every 428
      // regardless of current state yanked the user straight back to
      // /change-password right after they'd just succeeded — looked like
      // the screen "never opens" / loops. Only force the redirect if the
      // requirement is still actually true right now (localStorage is
      // updated synchronously by markPasswordChanged()/refreshMe()).
      if (path !== "/change-password" && getStoredUser()?.must_change_password) {
        window.location.assign("/change-password");
      }
    }
    return Promise.reject(error);
  }
);

// Plain-language text for failures that arrive without a readable message from
// the server (nginx/network/axios errors say things like "Request failed with
// status code 413" which means nothing to a user).
function friendlyHttpError(err) {
  const status = err?.response?.status;
  if (!err?.response) {
    if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
      return "This is taking too long. Please check your internet and try again.";
    }
    if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
      return "Can't reach the server. Please check your internet connection and try again.";
    }
    return null;
  }
  if (status === 413) return "The file is too large to upload. Please choose a smaller photo or file.";
  if (status === 403) return "You don't have permission to do this.";
  if (status === 404) return "This item was not found. It may have been removed — please refresh.";
  if (status === 408) return "This is taking too long. Please try again.";
  if (status === 429) return "Too many attempts. Please wait a minute and try again.";
  if (status === 502 || status === 503 || status === 504) {
    return "The server is busy or restarting. Please wait a minute and try again.";
  }
  if (status >= 500) return "Something went wrong on our side. Please try again; if it keeps happening, tell your admin.";
  return null;
}

export function formatApiError(err) {
  // Idempotent: fills err.response.data.detail from the v2 {error:{...}}
  // envelope when the interceptor didn't run (raw axios / manual calls).
  normalizeErrorEnvelope(err);
  const detail = err?.response?.data?.detail;
  if (!detail) {
    // Fallback: some endpoints return { error, message } at the top level
    // instead of nested under `detail`.
    const top = err?.response?.data;
    if (top && typeof top === "object") {
      if (typeof top.message === "string") return top.message;
      if (typeof top.error === "string") return top.error;
    }
    return friendlyHttpError(err) || err?.message || "Something went wrong. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => {
      if (!(d && typeof d.msg === "string")) return JSON.stringify(d);
      const field = Array.isArray(d.loc) ? String(d.loc[d.loc.length - 1] || "").replace(/_/g, " ") : "";
      return field && !/^\d+$/.test(field) && field !== "body" ? `${field}: ${d.msg}` : d.msg;
    }).join(" ");
  // PJP hotfix (2026-02): FastAPI HTTPException.detail may be a plain object
  // like {"error": "wrong_stage", "current_stage": "admin", "message": "..."}.
  // Previously we fell through to String(detail) → "[object Object]" toast.
  // Extract the human-readable message field in preference order.
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    // Hotfix — `detail.detail` (e.g. visits/start's geofence rejection:
    // {error: "outside_geo_fence", detail: "Outside allowed visit radius"})
    // is the human-readable string in practice; `detail.error` is the short
    // machine code. Prefer the readable one when both are present — this is
    // the only shape in the backend with both fields, so no other caller's
    // message changes.
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.error === "string") return detail.error;
    try { return JSON.stringify(detail); } catch (_) { /* fall through */ }
  }
  return String(detail);
}

// Path segment for GET /photos/{ref}. A stored ref looks like "gridfs://<id>"; its slashes would
// be URL-encoded (%2F) and Tomcat answers 400 to an encoded slash, so no photo would load. The API
// also accepts the bare <id> (PhotosController.normRef), so send that.
export function photoRefPath(ref) {
  return encodeURIComponent(String(ref || "").replace(/^gridfs:\/\//, ""));
}

// Legacy alias — some earlier code imported BACKEND_URL. Keep exported for
// backward compat but internal code should use API_BASE / RUNTIME_INFO.
export const BACKEND_URL = CONFIGURED_BACKEND_URL;
